import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Music, MessageSquare, LogOut, ShieldCheck } from "lucide-react";
import { useAuth, isBetaReviewer } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// MP10 — minimal layout for the Industry Beta Reviewer surface.
//
// Renders ONLY the two beta entry points (Catalog + My Feedback). The full
// internal `AppShell` (with admin navigation, audit log, settings, etc.) is
// never mounted for this role — it is impossible to navigate into any
// internal area from this shell because the routes themselves are gated.
// ---------------------------------------------------------------------------

function BetaShell() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Defence in depth — if a non-beta user somehow lands here, kick them out.
  if (!isBetaReviewer(user)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="beta-shell">
      <header className="sticky top-0 z-30 backdrop-blur bg-white/85 border-b" data-testid="beta-shell-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between gap-4">
          <Link
            to="/beta"
            className="inline-flex items-center gap-3 font-display text-lg tracking-tight"
            style={{ color: "hsl(347 60% 22%)" }}
            data-testid="beta-shell-home"
          >
            <ShieldCheck className="w-5 h-5" />
            Kandake — Industry Beta
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2" aria-label="Beta navigation">
            <NavLink
              to="/beta"
              end
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12.5px] font-medium ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-200"
                }`
              }
              data-testid="beta-nav-catalog"
            >
              <Music className="w-4 h-4" /> Catalog
            </NavLink>
            <NavLink
              to="/beta/my-feedback"
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2 rounded-md text-[12.5px] font-medium ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-200"
                }`
              }
              data-testid="beta-nav-myfeedback"
            >
              <MessageSquare className="w-4 h-4" /> My feedback
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-[13px] font-medium leading-tight" data-testid="beta-shell-user">{user.name || user.email}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Industry Beta Reviewer</div>
            </div>
            <Button
              variant="ghost" size="sm"
              onClick={() => { logout(); navigate("/login"); }}
              data-testid="beta-shell-logout"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-6">
        <Outlet />
      </main>
      <footer className="mt-12 py-6 border-t text-center text-[11.5px] text-muted-foreground">
        Kandake Compliance Services · Industry Beta access · invite-only<br/>
        Material shown here is for evaluation; no clearance, licence, or commercial use is granted.
      </footer>
    </div>
  );
}

export default BetaShell;
export { BetaShell };
